import React, { useState, useRef, useEffect } from "react";
import { Drawer, Box, Typography, CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import DentalChart from "./DentalChart";
import WorktypeChips from "../WorktypeChips";
import portals from "./portals.js";
import {
  worktypeMap,
  getWorktypeDisplayName,
  getBridgeUnitName,
  getMaterialName,
} from "./work-type-images.js";
import CloseIcon from "./CloseIcon";
import NextIcon from "../resources/icons/next.svg";
import PreviousIcon from "../resources/icons/previous.svg";
import { PdfPrintButton } from "./FileCategorySelector";
import PdfCanvasViewer from "./PdfCanvasViewer/PdfCanvasViewer";
import { MiscSwipeArea, ZoomableImage } from "./CaseMediaParts";
import { PDF_FADE_WITH_DRAWER } from "../featureFlags";

// Zelfde (gedeelde) chunk als in CaseDetailDrawer
const Draco = React.lazy(() => import("./Dracoloader"));

// Compacte case-weergave voor touch (telefoon, iPad staand): fullscreen, met
// tabs Info / Scans / IOS Form / Files i.p.v. de kaart naast de viewer. Alle
// state, laadlogica en case-guards blijven in CaseDetailDrawer (ctx); dit is
// alleen een andere layout daarvan.

const FONT = "'DM Sans', sans-serif";
const SAFE_TOP = "env(safe-area-inset-top, 0px)";
const SAFE_BOTTOM = "env(safe-area-inset-bottom, 0px)";
const THUMB = 56;
const EMPTY_PIXEL = "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";
// Horizontale veeg op Info = vorige/volgende case
const SWIPE_MIN_PX = 70;

const ellipsis = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const noTap = { WebkitTapHighlightColor: "transparent", touchAction: "manipulation" };

// Naam zonder geboortedatum/ID (die staan in de regel eronder)
function cleanPatientName(row) {
  return (row?.patientName || "")
    .replace(/\d{1,2}-\d{2}-\d{4}/g, "")
    .replace(/ID[:;]\s*\d+/gi, "")
    .replace("()", "")
    .replace(/\s+/g, " ")
    .trim();
}

// "geboortedatum • referentie", zelfde bron als de desktop-kaart
function patientMeta(row) {
  if (!row) return "";
  const dateMatch = (row.patientName || "").match(/\d{1,2}-\d{2}-\d{4}/);
  const idMatch = (row.patientName || "").match(/ID[:;]\s*(\d+)/i);
  const birthday = (dateMatch ? dateMatch[0].replace(/-/g, "/") : null) || row.birthday;
  const ref = (row.ref || "").trim() || (idMatch ? idMatch[1] : null);
  return [birthday, ref].filter(Boolean).join(" • ");
}

function formatIncoming(ts) {
  try {
    return new Date(ts).toLocaleString("nl-NL", {
      hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    }).replace(/-/g, "/");
  } catch { return "N/A"; }
}

function formatDelivery(ts) {
  if (!ts) return "N/A";
  try {
    return new Date(ts).toLocaleDateString("nl-NL", { year: "numeric", month: "2-digit", day: "2-digit" }).replace(/-/g, "/");
  } catch { return "N/A"; }
}

function cleanRemark(remark) {
  return remark
    .replace(/^"|"$/g, "")
    .replace(/\\n/g, "\n")
    .replace(/&gt;/g, ">")
    .replace(/&lt;/g, "<")
    .replace(/&amp;/g, "&");
}

function RoundButton({ onClick, disabled, label, children, active }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      style={{
        ...noTap,
        width: 44, height: 44, flexShrink: 0, borderRadius: "50%", padding: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        border: "1px solid rgba(255,255,255,0.10)",
        background: active ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.10)",
        opacity: disabled ? 0.35 : 1,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div
      role="tablist"
      style={{
        display: "flex", gap: 4, padding: 4, borderRadius: 14,
        background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      {tabs.map((t) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.key)}
            style={{
              ...noTap,
              flex: 1, minWidth: 0, height: 36, border: 0, borderRadius: 10, padding: "0 6px",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              background: isActive ? "rgba(255,255,255,0.16)" : "transparent",
              color: isActive ? "#FFF" : "rgba(255,255,255,0.6)",
              fontFamily: FONT, fontSize: 14, fontWeight: 500,
              transition: "background 0.2s ease, color 0.2s ease",
              cursor: "pointer",
            }}
          >
            <span style={ellipsis}>{t.label}</span>
            {t.badge != null && (
              <span style={{ fontSize: 11, fontWeight: 600, lineHeight: "16px", padding: "0 5px", borderRadius: 8, background: "rgba(255,255,255,0.14)", color: "rgba(255,255,255,0.8)" }}>
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Eén worktype-regel, zelfde vorm als op de desktop-kaart
function WorktypeRow({ img, badge, badgeDark, title, subtitle, indent }) {
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 10, height: 52, padding: 4, marginLeft: indent ? 12 : 0,
        borderRadius: 50, background: "linear-gradient(90deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 100%)",
      }}
    >
      <div style={{ position: "relative", width: 44, height: 44, flexShrink: 0, borderRadius: "50%", overflow: "hidden", background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)" }}>
        <img src={img || EMPTY_PIXEL} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.src = EMPTY_PIXEL; }} />
        <span style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 500, color: badgeDark ? "#000" : "#fff" }}>
          {badge}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
        <span style={{ ...ellipsis, color: "#FFF", fontFamily: FONT, fontSize: 15, fontWeight: 500, lineHeight: "18px" }}>{title}</span>
        {subtitle && <span style={{ ...ellipsis, color: "rgba(255,255,255,0.6)", fontFamily: FONT, fontSize: 13, lineHeight: "16px" }}>{subtitle}</span>}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{ fontFamily: FONT, fontSize: 20, fontWeight: 500, color: "#FFF", mt: 3, mb: 1.5 }}>
      {children}
    </Typography>
  );
}

// Laag per tab: alles blijft gemount (Draco, pdf-viewers, en de DentalChart
// die de tand-plaatjes levert), alleen de actieve is zichtbaar
const paneStyle = (active) => ({
  position: "absolute", inset: 0,
  opacity: active ? 1 : 0,
  visibility: active ? "visible" : "hidden",
  pointerEvents: active ? "auto" : "none",
  transition: active ? "opacity 0.2s ease, visibility 0s" : "opacity 0.2s ease, visibility 0s linear 0.2s",
});

export default function CaseDetailTouch({ ctx }) {
  const {
    open, onClose, rowData,
    goNewer, goOlder, isNewerRowDisabled, isOlderRowDisabled, isLoading, awaitingNext,
    contentOpacity, isTransitioning,
    localStatus,
    chartRef, dentalHighlights, highlightPreviews, jawBased, bridges, singles, bgImage,
    fileCategory, setFileCategory, hasOrderForm, hasMiscFiles,
    viewerData, noScans, scanMode, onScanModeChange, holdScanLoading, onScansSettled,
    orderFormPdfViewer, miscPdfViewer, activePdfViewer, orderFormMissing,
    shownMiscFile, shownMiscFiles, selectedMiscFile, selectMiscFile, handleMiscSwipe,
    miscUrls, loadedMiscIds, miscErrorIds, markMiscLoaded, markMiscError, miscNavDirection, allThumbsReady,
  } = ctx;

  // Zolang dit open is de app eronder verbergen en de achtergrondvideo
  // pauzeren: die bleef (onzichtbaar) doorspelen, met de geblurde lijst
  // (backdrop-filter) erboven die iOS elk videoframe opnieuw moest berekenen.
  // Dat maakte de scans en de pdf hier traag en haperig.
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add("case-touch-open");
    const video = document.querySelector(".background-video");
    const wasPlaying = !!video && !video.paused;
    if (wasPlaying) video.pause();
    // iOS Safari: een pinch is óók een eigen "gesture" (pagina-zoom) die het
    // gebaar overneemt en de pointer-events afbreekt — dan zoomt de pdf niet
    // en hapert pinchen in de scans. Hier zelf afhandelen (pdf) of negeren.
    const stopGesture = (e) => e.preventDefault();
    const gestureTypes = ["gesturestart", "gesturechange", "gestureend"];
    gestureTypes.forEach((t) => document.addEventListener(t, stopGesture, { passive: false }));
    return () => {
      gestureTypes.forEach((t) => document.removeEventListener(t, stopGesture));
      document.body.classList.remove("case-touch-open");
      if (wasPlaying && video.isConnected) video.play().catch(() => {});
    };
  }, [open]);

  // "info" of "content"; bij content bepaalt fileCategory (van de drawer)
  // welke. Zo volgt de tab vanzelf als de drawer bij een case-wissel Files
  // terugzet naar Scans.
  const [pane, setPane] = useState("info");
  const activeTab = pane === "info" ? "info" : fileCategory;
  const selectTab = (key) => {
    if (key === "info") { setPane("info"); return; }
    setPane("content");
    setFileCategory(key);
  };

  const tabs = [
    { key: "info", label: "Info" },
    { key: "scans", label: "Scans" },
    (hasOrderForm || activeTab === "order-form") && { key: "order-form", label: "IOS Form" },
    (hasMiscFiles || activeTab === "misc-files") && { key: "misc-files", label: "Files", badge: shownMiscFiles.length > 0 ? shownMiscFiles.length : null },
  ].filter(Boolean);

  // Veeg op Info: links = volgende (oudere) case, rechts = vorige (nieuwere)
  const swipeRef = useRef(null);
  const onInfoPointerDown = (e) => {
    if (e.pointerType !== "touch") return;
    swipeRef.current = { x: e.clientX, y: e.clientY, t: performance.now() };
  };
  const onInfoPointerUp = (e) => {
    const s = swipeRef.current;
    swipeRef.current = null;
    if (!s || e.pointerType !== "touch") return;
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * 1.5 || performance.now() - s.t > 800) return;
    if (dx < 0 && !isOlderRowDisabled) goOlder();
    else if (dx > 0 && !isNewerRowDisabled) goNewer();
  };

  const isOrderFormTab = activeTab === "order-form";
  const isFilesTab = activeTab === "misc-files";
  const showPrint = (isOrderFormTab || (isFilesTab && !!shownMiscFile?.isPdf)) && contentOpacity === 1 && activePdfViewer.contentReady;
  const notes = rowData.remark ? cleanRemark(rowData.remark) : "";
  const meta = patientMeta(rowData);
  const practice = (rowData.clinic || "").trim();
  const showPractice = practice && practice.toLowerCase() !== (rowData.dentist || "").trim().toLowerCase();
  const hasWorktypes = jawBased.length + bridges.length + singles.length > 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      ModalProps={{ keepMounted: false }}
      slotProps={{
        paper: {
          sx: {
            width: "100%", maxWidth: "100%", height: "100%",
            background: "linear-gradient(180deg, #17131f 0%, #09090d 60%)",
            color: "#fff", overflow: "hidden", outline: "none",
          },
        },
      }}
    >
      <Box
        className="case-touch-root"
        sx={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          pt: SAFE_TOP, pl: "env(safe-area-inset-left, 0px)", pr: "env(safe-area-inset-right, 0px)",
        }}
      >
        {/* Kop: sluiten, patiënt (altijd zichtbaar, op elke tab), vorige/volgende */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", flexShrink: 0 }}>
          <RoundButton label="Sluiten" onClick={onClose}><CloseIcon /></RoundButton>
          <div style={{ flex: 1, minWidth: 0, textAlign: "center", opacity: contentOpacity, transition: "opacity 0.2s ease" }}>
            <div style={{ ...ellipsis, fontFamily: FONT, fontSize: 16, fontWeight: 600, lineHeight: "20px" }}>
              {cleanPatientName(rowData)}
            </div>
            <div style={{ ...ellipsis, fontFamily: FONT, fontSize: 12, lineHeight: "16px", color: "rgba(255,255,255,0.5)" }}>
              {meta}
            </div>
          </div>
          <RoundButton label="Vorige case" onClick={goNewer} disabled={isNewerRowDisabled}>
            <img src={PreviousIcon} alt="" draggable={false} style={{ width: 16, height: 16 }} />
          </RoundButton>
          <RoundButton label="Volgende case" onClick={goOlder} disabled={isOlderRowDisabled}>
            {isLoading && awaitingNext
              ? <CircularProgress size={16} thickness={3} sx={{ color: "rgba(255,255,255,0.8)" }} />
              : <img src={NextIcon} alt="" draggable={false} style={{ width: 16, height: 16 }} />}
          </RoundButton>
        </div>

        <div style={{ padding: "0 12px 8px", flexShrink: 0 }}>
          <TabBar tabs={tabs} active={activeTab} onChange={selectTab} />
        </div>

        <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
          {/* ── Info ─────────────────────────────────────────────── */}
          <div style={paneStyle(activeTab === "info")}>
            <div
              onPointerDown={onInfoPointerDown}
              onPointerUp={onInfoPointerUp}
              onPointerCancel={() => { swipeRef.current = null; }}
              style={{
                position: "absolute", inset: 0, overflowY: "auto", overflowX: "hidden",
                WebkitOverflowScrolling: "touch", touchAction: "pan-y", overscrollBehavior: "contain",
                padding: `8px 20px calc(${SAFE_BOTTOM} + 24px)`,
              }}
            >
              {/* Worktype-afbeelding als achtergrond, zoals op de kaart */}
              {bgImage && (
                <img
                  src={bgImage.src}
                  srcSet={bgImage.srcSet}
                  alt=""
                  draggable={false}
                  style={{ position: "absolute", top: 0, right: 0, width: 280, height: "auto", opacity: 0.3 * contentOpacity, pointerEvents: "none", transition: "opacity 0.2s ease" }}
                />
              )}
              <div style={{ position: "relative", opacity: contentOpacity, transition: "opacity 0.2s ease" }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 34, fontWeight: 500, lineHeight: "36px", userSelect: "text", pr: 2 }}>
                  {cleanPatientName(rowData)}
                </Typography>
                {meta && (
                  <Typography sx={{ fontFamily: FONT, fontSize: 15, color: "rgba(255,255,255,0.6)", mt: 1, userSelect: "text" }}>
                    {meta}
                  </Typography>
                )}
                <Box sx={{ mt: 2, ml: localStatus === "accepted" ? 0 : "-6px" }}>
                  <WorktypeChips row={{ ...rowData, status: localStatus }} worktypeMap={worktypeMap} brand={rowData.scannerBrand} portals={portals} layoutAnimations />
                </Box>
                <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 500, mt: 2.5, userSelect: "text" }}>
                  {rowData.dentist}
                </Typography>
                {showPractice && (
                  <Typography sx={{ fontFamily: FONT, fontSize: 14, color: "rgba(255,255,255,0.6)", userSelect: "text" }}>
                    {rowData.clinic}
                  </Typography>
                )}

                <div style={{ marginTop: 20, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                  {[["Incoming", formatIncoming(rowData.date), false], ["Outgoing", formatDelivery(rowData.deliverydate), true]].map(([label, value, strong]) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: 44, borderBottom: "1px solid rgba(255,255,255,0.1)", fontFamily: FONT, fontSize: 14 }}>
                      <span style={{ color: "rgba(255,255,255,0.5)" }}>{label}</span>
                      <span style={{ color: "#FFF", fontWeight: strong ? 500 : 400 }}>{value}</span>
                    </div>
                  ))}
                </div>

                {hasWorktypes && (
                  <>
                    <SectionTitle>Worktype</SectionTitle>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {jawBased.map((item, idx) => (
                        <WorktypeRow
                          key={`jaw-${idx}`}
                          img={highlightPreviews[item.jaw]?.dataUrl}
                          badge={item.jaw}
                          title={getWorktypeDisplayName(item.worktype)}
                          subtitle={[item.worktype.MaterialID, item.worktype.SettingID, item.worktype.Shade].filter(Boolean).join(" • ")}
                        />
                      ))}
                      {bridges.map((bridge, bridgeIdx) => (
                        <div key={`bridge-${bridgeIdx}`} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: "0.5px", padding: "4px 16px 0" }}>
                            {(bridge?.worktype?.RestorationTypeID || "").replace(/([A-Z])/g, " $1").trim()} {bridge?.fromTo || ""}
                          </span>
                          {bridge.units?.map((unit) => (
                            <WorktypeRow
                              key={`unit-${unit.num}`}
                              indent
                              img={unit.preview?.dataUrl}
                              badge={unit.num}
                              badgeDark
                              title={unit.preview?.worktype ? getBridgeUnitName(unit.preview.worktype) : ""}
                              subtitle={unit.preview?.worktype ? [getMaterialName(unit.preview.worktype), unit.preview.worktype.SettingID, unit.preview.worktype.Shade].filter(Boolean).join(" • ") : ""}
                            />
                          ))}
                        </div>
                      ))}
                      {singles.map(({ num, preview, worktype }) => (
                        <WorktypeRow
                          key={`single-${num}`}
                          img={preview?.dataUrl}
                          badge={num}
                          badgeDark={!isNaN(num)}
                          title={worktype ? getWorktypeDisplayName(worktype) : ""}
                          subtitle={worktype ? [getMaterialName(worktype), worktype.SettingID, worktype.Shade].filter(Boolean).join(" • ") : ""}
                        />
                      ))}
                    </div>
                  </>
                )}

                {notes && (
                  <>
                    <SectionTitle>Note</SectionTitle>
                    <Typography sx={{ fontFamily: FONT, fontSize: 15, lineHeight: "140%", color: "rgba(255,255,255,0.7)", whiteSpace: "pre-wrap", borderLeft: "1px solid rgba(255,255,255,0.1)", pl: 1.5, userSelect: "text" }}>
                      {notes}
                    </Typography>
                  </>
                )}
              </div>

              {/* Tandenschema. Altijd gemount: levert ook de tand-plaatjes
                  van de worktypes hierboven (chartRef). */}
              <div style={{ display: "flex", justifyContent: "center", marginTop: 24, opacity: contentOpacity, transition: "opacity 0.2s ease", pointerEvents: "none" }}>
                <div style={{ width: 205, height: 320 }}>
                  <DentalChart ref={chartRef} style={{ width: "100%", height: "100%" }} initialHighlights={dentalHighlights} />
                </div>
              </div>
            </div>
          </div>

          {/* ── Scans ────────────────────────────────────────────── */}
          <div style={{ ...paneStyle(activeTab === "scans"), bottom: SAFE_BOTTOM }}>
            <React.Suspense
              fallback={
                <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CircularProgress sx={{ color: "rgba(255,255,255,0.8)" }} />
                </Box>
              }
            >
              <Draco
                touch
                viewerData={viewerData}
                expanded
                noScans={noScans}
                hidden={activeTab !== "scans" || !open}
                scanMode={scanMode}
                onScanModeChange={onScanModeChange}
                holdLoading={holdScanLoading}
                caseKey={rowData?.id ?? null}
                worktypes={rowData?.worktypes ?? null}
                onLoadSettled={onScansSettled}
              />
            </React.Suspense>
          </div>

          {/* ── IOS Form ─────────────────────────────────────────── */}
          <div style={paneStyle(isOrderFormTab)}>
            <div style={{ position: "absolute", top: 0, left: 8, right: 8, bottom: `calc(${SAFE_BOTTOM} + 12px)` }}>
              {orderFormMissing ? (
                <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.7)", fontFamily: FONT }}>
                  Geen IOS Form voor deze case
                </div>
              ) : (
                <div
                  style={{
                    position: "relative", width: "100%", height: "100%",
                    ...(PDF_FADE_WITH_DRAWER && { opacity: isTransitioning ? 0 : 1, transition: "opacity 0.2s ease-in-out" }),
                  }}
                >
                  <PdfCanvasViewer viewer={orderFormPdfViewer} suppressSpinner={contentOpacity === 0} />
                </div>
              )}
            </div>
          </div>

          {/* ── Files ────────────────────────────────────────────── */}
          <div style={paneStyle(isFilesTab)}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: `calc(${SAFE_BOTTOM} + ${THUMB + 28}px)`, overflow: "hidden" }}>
              {shownMiscFile && !miscErrorIds.has(shownMiscFile.id) && !shownMiscFile.isPdf && !loadedMiscIds.has(shownMiscFile.id) && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                  <CircularProgress sx={{ color: "rgba(255,255,255,0.8)" }} />
                </div>
              )}
              {shownMiscFile && miscErrorIds.has(shownMiscFile.id) && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.8)", fontFamily: FONT }}>
                  Failed to load file
                </div>
              )}
              {shownMiscFile && !miscErrorIds.has(shownMiscFile.id) && (
                <AnimatePresence initial={false} custom={miscNavDirection}>
                  <motion.div
                    key={shownMiscFile.id}
                    custom={miscNavDirection}
                    variants={{
                      enter: (dir) => ({ x: `${dir * 100}%`, zIndex: 1 }),
                      center: { x: 0, zIndex: 1 },
                      exit: (dir) => ({ x: `${-dir * 100}%`, zIndex: 0 }),
                    }}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
                    style={{ position: "absolute", inset: 0 }}
                  >
                    <MiscSwipeArea
                      touch
                      enabled={shownMiscFiles.length > 1 && !shownMiscFile.isPdf}
                      onSwipe={handleMiscSwipe}
                      style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", touchAction: "pan-y", padding: "0 8px", boxSizing: "border-box" }}
                    >
                      {shownMiscFile.isPdf ? (
                        <div style={{ position: "relative", width: "100%", height: "100%" }}>
                          <PdfCanvasViewer viewer={miscPdfViewer} suppressSpinner={contentOpacity === 0} />
                        </div>
                      ) : (
                        miscUrls[shownMiscFile.id] && (
                          <ZoomableImage
                            touch
                            src={miscUrls[shownMiscFile.id]}
                            alt={shownMiscFile.filename}
                            draggable={false}
                            onLoad={() => markMiscLoaded(shownMiscFile.id)}
                            onError={() => markMiscError(shownMiscFile.id)}
                            sx={{
                              maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: "8px",
                              opacity: loadedMiscIds.has(shownMiscFile.id) ? 1 : 0, transition: "opacity 0.25s ease",
                            }}
                          />
                        )
                      )}
                    </MiscSwipeArea>
                  </motion.div>
                </AnimatePresence>
              )}
            </div>

            {/* Thumbnails, horizontaal scrollbaar */}
            {shownMiscFiles.length > 1 && (
              <div
                style={{
                  position: "absolute", left: 0, right: 0, bottom: `calc(${SAFE_BOTTOM} + 12px)`, height: THUMB,
                  display: "flex", gap: 8, padding: "0 12px", overflowX: "auto", overflowY: "hidden",
                  scrollbarWidth: "none", WebkitOverflowScrolling: "touch",
                  justifyContent: shownMiscFiles.length * (THUMB + 8) < window.innerWidth - 24 ? "center" : "flex-start",
                }}
              >
                {!allThumbsReady ? (
                  <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress size={22} sx={{ color: "rgba(255,255,255,0.6)" }} />
                  </div>
                ) : shownMiscFiles.map((f) => {
                  const isActive = selectedMiscFile?.id === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => { if (!isActive) selectMiscFile(f); }}
                      style={{
                        ...noTap,
                        position: "relative", width: THUMB, height: THUMB, flexShrink: 0, padding: 0, border: 0,
                        borderRadius: 8, overflow: "hidden", background: "rgba(30,41,59,0.6)",
                        opacity: isActive ? 1 : 0.6, transition: "opacity 0.2s ease",
                        cursor: "pointer",
                      }}
                    >
                      {/* Rand als laag erboven, zodat 'ie over het plaatje valt */}
                      <span style={{ position: "absolute", inset: 0, borderRadius: 8, pointerEvents: "none", zIndex: 1, boxShadow: isActive ? "inset 0 0 0 2px rgba(255,255,255,0.9)" : "inset 0 0 0 1px rgba(255,255,255,0.2)" }} />
                      {f.isImage && miscUrls[f.id] ? (
                        <img src={miscUrls[f.id]} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      ) : (
                        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 500, letterSpacing: "0.72px", textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>
                          {f.ext || "file"}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Printen, rechtsboven bij een pdf */}
          <div style={{ position: "absolute", top: 8, right: 16, zIndex: 5 }}>
            <PdfPrintButton visible={showPrint} disabled={!activePdfViewer.contentReady} onClick={activePdfViewer.actions.print} />
          </div>
        </div>
      </Box>
    </Drawer>
  );
}
